
import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPlayCircle,
  faClock,
  faExchangeAlt,
  faPhoneVolume,
  faPlus,
  faTrash,
  faLayerGroup
} from '@fortawesome/free-solid-svg-icons';

import AudioSelectorUploads from '../../Audio/AudioSelectorUploads';
import TimeConditionManager from '../../shared/TimeConditionManager';
import * as URAApi from '../../../../services/uraApi';
import '../URAComponents.css';
import { isDestinationContext } from '../../../../services/destinationContexts';
import { integerInputValue, integerOrDefault, parseIntegerInput } from '../../../../services/integerInput';

function MenuConfiguration({ config, onChange, tenant, hasWhatsAppIntegration = false, availableQueues = [], availableContexts = [] }) {
  
  const [availableTrunks, setAvailableTrunks] = useState([]);
  const [loadingTrunks, setLoadingTrunks] = useState(false);
  
  console.log('🔧 [MENU-CONFIG] Renderizando com tenant:', tenant, 'config:', config);
  console.log('📋 [MENU-CONFIG] Filas disponíveis:', availableQueues.length);
  console.log('📋 [MENU-CONFIG] Contextos disponíveis:', availableContexts.length);
  
  useEffect(() => {
    if (tenant) {
      loadAvailableTrunks();
    }
  }, [tenant]);
  
  const loadAvailableTrunks = async () => {
    setLoadingTrunks(true);
    try {
      const response = await URAApi.getAvailableTrunks(tenant);
      if (response.success && response.data) {
        const filteredTrunks = response.data.filter(trunk => 
          trunk.accountcode === tenant || trunk.tenant === tenant
        );
        setAvailableTrunks(filteredTrunks);
        console.log(`✅ [MENU-CONFIG] Troncos carregados: ${filteredTrunks.length} de ${response.data.length} (filtrados por tenant: ${tenant})`);
      }
    } catch (error) {
      console.error('❌ [MENU-CONFIG] Erro ao carregar troncos:', error);
    } finally {
      setLoadingTrunks(false);
    }
  };
  
  const handleChange = (field, value) => {
    console.log(`📝 [MENU-CONFIG] Campo alterado: ${field} = ${value}`);
    onChange({ ...config, [field]: value });
  };
  
  const audioMethods = [
    { value: 'background', label: 'Background', description: 'Permite dígito durante o áudio' },
    { value: 'playback', label: 'Playback', description: 'Aguarda término do áudio' }
  ];
  
  const destinationTypes = [
    { value: 'endpoint', label: 'Ramal' },
    { value: 'queue', label: 'Fila' },
    { value: 'external', label: 'Externo' },
    { value: 'disa', label: 'DISA' },
    { value: 'whatsapp', label: 'WhatsApp', disabled: !hasWhatsAppIntegration },
    { value: 'cycle', label: 'Ciclo (Reiniciar Menu)' },
    { value: 'hangup', label: 'Desligar' }
  ];
  
  const overflowDestinationTypes = [
    { value: 'endpoint', label: 'Ramal' },
    { value: 'queue', label: 'Fila' },
    { value: 'external', label: 'Externo' },
    { value: 'disa', label: 'DISA' },
    { value: 'whatsapp', label: 'WhatsApp', disabled: !hasWhatsAppIntegration },
    { value: 'cycle', label: 'Ciclo' },
    { value: 'hangup', label: 'Desligar' }
  ];
  
  const handleAddWaitextenOverflow = () => {
    const currentOverflows = config.waitexten_overflows || [];
    const newOverflows = [...currentOverflows, {
      timeout_seconds: 30,
      destination_type: 'endpoint',
      destination_overflow: '',
      audio_enabled: false,
      audio_file: '',
      audio_type: '',
      audio_accountcode: tenant,
      hangup_after_audio: false
    }];
    handleChange('waitexten_overflows', newOverflows);
  };
  
  const handleRemoveWaitextenOverflow = (index) => {
    const currentOverflows = config.waitexten_overflows || [];
    const newOverflows = currentOverflows.filter((_, i) => i !== index);
    handleChange('waitexten_overflows', newOverflows);
  };
  
  const handleUpdateWaitextenOverflow = (index, field, value) => {
    const currentOverflows = config.waitexten_overflows || [];
    const newOverflows = [...currentOverflows];
    newOverflows[index] = {
      ...newOverflows[index],
      [field]: value
    };
    
    if (field === 'destination_type' && ['cycle', 'whatsapp', 'hangup'].includes(value)) {
      newOverflows[index].destination_overflow = '';
    }
    
    handleChange('waitexten_overflows', newOverflows);
  };

  return (
    <div className="menu-config-card">
      <h3 className="section-title">
        <FontAwesomeIcon icon={faPhoneVolume} className="section-icon" />
        Configuração do Menu Principal
      </h3>
      
      <div className="form-field">
        <label className="form-label">Extensão do Menu</label>
        <input
          className="form-input"
          type="text"
          placeholder="menu"
          value={config.menu_extension || 'menu'}
          onChange={(e) => handleChange('menu_extension', e.target.value)}
        />
        <p className="form-help">Padrão: "menu". Pode ser alterado se necessário.</p>
      </div>
      
      <div className="form-checkbox-wrapper">
        <input
          type="checkbox"
          id="menu-audio-enabled"
          checked={config.menu_audio_enabled || false}
          onChange={(e) => handleChange('menu_audio_enabled', e.target.checked)}
        />
        <label htmlFor="menu-audio-enabled">Reproduzir áudio do menu</label>
      </div>
      
      {config.menu_audio_enabled && (
        <>
          <AudioSelectorUploads
            tenant={tenant}
            selectedFile={config.menu_audio_file}
            selectedType={config.menu_audio_type}
            onAudioChange={(audioData) => {
              console.log('🎵 [MENU-CONFIG] Áudio selecionado:', audioData);
              onChange({
                ...config,
                menu_audio_file: audioData.file,
                menu_audio_type: audioData.type,
                menu_audio_accountcode: audioData.accountcode
              });
            }}
          />
          <p className="form-help" style={{ marginTop: '-10px', marginBottom: '15px' }}>
            <strong>*Obrigatório:</strong> Selecione um áudio para reproduzir no menu. Se não houver áudios, faça upload em Configurações &gt; Uploads.
          </p>
          
          <div className="form-field">
            <label className="form-label">Método de Reprodução</label>
            <select
              className="form-select"
              value={config.menu_audio_method || 'background'}
              onChange={(e) => handleChange('menu_audio_method', e.target.value)}
            >
              {audioMethods.map(method => (
                <option key={method.value} value={method.value}>
                  {method.label} - {method.description}
                </option>
              ))}
            </select>
            <p className="form-help">
              <strong>Background:</strong> Usuário pode digitar durante o áudio<br />
              <strong>Playback:</strong> Aguarda o áudio terminar
            </p>
          </div>
        </>
      )}
      
      <hr className="section-divider" />
      
      <h4 className="section-title" style={{fontSize: '1.1rem'}}>
        <FontAwesomeIcon icon={faClock} className="section-icon" />
        Aguardar Dígito (WaitExten)
      </h4>
      
      <div className="form-field">
        <label className="form-label">Timeout (segundos)</label>
        <input
          className="form-input"
          type="number"
          min="5"
          max="60"
          value={integerInputValue(config.waitexten_timeout, 10)}
          onChange={(e) => handleChange('waitexten_timeout', parseIntegerInput(e.target.value))}
          onBlur={(e) => handleChange('waitexten_timeout', integerOrDefault(e.target.value, 10))}
        />
        <p className="form-help">Tempo de espera por um dígito (5-60 segundos)</p>
      </div>
      
      <div className="form-field">
        <label className="form-label">Ação após Timeout</label>
        <select
          className="form-select"
          value={config.waitexten_destination_type || 'hangup'}
          onChange={(e) => handleChange('waitexten_destination_type', e.target.value)}
        >
          {destinationTypes.map(type => (
            <option 
              key={type.value} 
              value={type.value}
              disabled={type.disabled}
            >
              {type.label} {type.disabled ? '(sem integração)' : ''}
            </option>
          ))}
        </select>
      </div>
      
      {!['hangup', 'whatsapp', 'cycle'].includes(config.waitexten_destination_type) && (
        <div className="form-field">
          <label className="form-label">Destino</label>
          {config.waitexten_destination_type === 'queue' ? (
            <select
              className="form-select"
              value={config.waitexten_destination || ''}
              onChange={(e) => handleChange('waitexten_destination', e.target.value)}
            >
              <option value="">Selecione uma fila</option>
              {availableQueues.map((queue) => (
                <option key={queue.namequeue || queue.name} value={queue.namequeue || queue.name}>
                  {queue.namequeue || queue.name}
                </option>
              ))}
            </select>
          ) : config.waitexten_destination_type === 'disa' ? (
            <select
              className="form-select"
              value={config.waitexten_destination || ''}
              onChange={(e) => handleChange('waitexten_destination', e.target.value)}
            >
              <option value="">Selecione um contexto</option>
              {availableContexts.filter(context => {
                const contextName = (context.name || context.context_name || '').toLowerCase();
                return isDestinationContext(contextName);
              }).map((context) => (
                <option key={context.name || context.context_name} value={context.name || context.context_name}>
                  {context.label || context.name || context.context_name}
                </option>
              ))}
            </select>
          ) : config.waitexten_destination_type === 'external' ? (
            <div className="field-horizontal-wrapper">
              <div className="form-field">
                <label className="form-label">Número Externo</label>
                <input
                  className="form-input"
                  type="text"
                  placeholder="Ex: 1140041234 ou 01140041234"
                  value={config.waitexten_external_number || ''}
                  onChange={(e) => handleChange('waitexten_external_number', e.target.value)}
                />
              </div>
              
              <div className="form-field" style={{minWidth: '200px'}}>
                <label className="form-label">Tronco</label>
                <select
                  className="form-select"
                  value={config.waitexten_external_trunk || ''}
                  onChange={(e) => handleChange('waitexten_external_trunk', e.target.value)}
                  disabled={loadingTrunks}
                >
                  <option value="">Selecione o tronco</option>
                  {availableTrunks.map((trunk) => (
                    <option key={trunk.name} value={trunk.name}>
                      {trunk.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          ) : (
            <input
              className="form-input"
              type="text"
              placeholder={
                config.waitexten_destination_type === 'endpoint' ? 'Ex: 2000' :
                'Destino'
              }
              value={config.waitexten_destination || ''}
              onChange={(e) => handleChange('waitexten_destination', e.target.value)}
            />
          )}
          {config.waitexten_destination_type === 'external' && (
            <p className="form-help">
              💡 <strong>Dica:</strong> Insira o telefone (externo) de acordo com a discagem da operadora
            </p>
          )}
          {config.waitexten_destination_type === 'queue' && availableQueues.length === 0 && (
            <p className="form-help" style={{color: '#f39c12'}}>Nenhuma fila encontrada para este tenant. Crie filas em Configurações &gt; Filas.</p>
          )}
          {config.waitexten_destination_type === 'disa' && availableContexts.length === 0 && (
            <p className="form-help" style={{color: '#f39c12'}}>Nenhum contexto encontrado. Crie contextos em Dialplan.</p>
          )}
        </div>
      )}
      
      <hr className="section-divider" />
      
      <div className="overflow-section">
        <div className="overflow-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
          <h4 className="section-title" style={{fontSize: '1.1rem', margin: 0}}>
            <FontAwesomeIcon icon={faLayerGroup} className="section-icon" />
            Transbordos do Timeout
          </h4>
          <button
            type="button"
            className="button is-small is-info"
            onClick={handleAddWaitextenOverflow}
          >
            <FontAwesomeIcon icon={faPlus} style={{ marginRight: '6px' }} />
            Adicionar Transbordo
          </button>
        </div>
        <p className="form-help" style={{ marginBottom: '15px' }}>
          Configure destinos em cascata caso o destino principal não atenda. Cada transbordo tem seu próprio timeout.
        </p>
        
        {(!config.waitexten_overflows || config.waitexten_overflows.length === 0) ? (
          <p className="form-help" style={{ fontStyle: 'italic', color: '#888' }}>
            Nenhum transbordo configurado. O destino principal será o único tentado.
          </p>
        ) : (
          config.waitexten_overflows.map((overflow, index) => (
            <div key={index} className="box" style={{ backgroundColor: '#fff', padding: '15px', marginBottom: '10px', border: '1px solid #ddd', borderRadius: '4px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <strong>Transbordo {index + 1}</strong>
                <button
                  type="button"
                  className="button is-small is-danger"
                  onClick={() => handleRemoveWaitextenOverflow(index)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                <div className="form-field" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.85rem' }}>Timeout (segundos)</label>
                  <input
                    className="form-input"
                    type="number"
                    min="1"
                    max="300"
                    value={integerInputValue(overflow.timeout_seconds, 30)}
                    onChange={(e) => handleUpdateWaitextenOverflow(index, 'timeout_seconds', parseIntegerInput(e.target.value))}
                    onBlur={(e) => handleUpdateWaitextenOverflow(index, 'timeout_seconds', integerOrDefault(e.target.value, 30))}
                  />
                </div>
                
                <div className="form-field" style={{ marginBottom: 0 }}>
                  <label className="form-label" style={{ fontSize: '0.85rem' }}>Tipo</label>
                  <select
                    className="form-select"
                    value={overflow.destination_type || 'endpoint'}
                    onChange={(e) => handleUpdateWaitextenOverflow(index, 'destination_type', e.target.value)}
                  >
                    {overflowDestinationTypes.map(type => (
                      <option key={type.value} value={type.value} disabled={type.disabled}>
                        {type.label} {type.disabled ? '(sem integração)' : ''}
                      </option>
                    ))}
                  </select>
                </div>
                
                {!['hangup', 'whatsapp', 'cycle'].includes(overflow.destination_type) && (
                  <div className="form-field" style={{ marginBottom: 0 }}>
                    <label className="form-label" style={{ fontSize: '0.85rem' }}>Destino</label>
                    {overflow.destination_type === 'queue' ? (
                      <select
                        className="form-select"
                        value={overflow.destination_overflow || ''}
                        onChange={(e) => handleUpdateWaitextenOverflow(index, 'destination_overflow', e.target.value)}
                      >
                        <option value="">Selecione uma fila</option>
                        {availableQueues.map((queue) => (
                          <option key={queue.namequeue || queue.name} value={queue.namequeue || queue.name}>
                            {queue.namequeue || queue.name}
                          </option>
                        ))}
                      </select>
                    ) : overflow.destination_type === 'disa' ? (
                      <select
                        className="form-select"
                        value={overflow.destination_overflow || ''}
                        onChange={(e) => handleUpdateWaitextenOverflow(index, 'destination_overflow', e.target.value)}
                      >
                        <option value="">Selecione um contexto</option>
                        {availableContexts.filter(context => {
                          const contextName = (context.name || context.context_name || '').toLowerCase();
                          return isDestinationContext(contextName);
                        }).map((context) => (
                          <option key={context.name || context.context_name} value={context.name || context.context_name}>
                            {context.label || context.name || context.context_name}
                          </option>
                        ))}
                      </select>
                    ) : overflow.destination_type === 'external' ? (
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <input
                          className="form-input"
                          type="text"
                          placeholder="Número"
                          style={{ flex: 1 }}
                          value={overflow.overflow_external_number || ''}
                          onChange={(e) => handleUpdateWaitextenOverflow(index, 'overflow_external_number', e.target.value)}
                        />
                        <select
                          className="form-select"
                          style={{ width: '120px' }}
                          value={overflow.overflow_external_trunk || ''}
                          onChange={(e) => handleUpdateWaitextenOverflow(index, 'overflow_external_trunk', e.target.value)}
                          disabled={loadingTrunks}
                        >
                          <option value="">Tronco</option>
                          {availableTrunks.map((trunk) => (
                            <option key={trunk.name} value={trunk.name}>
                              {trunk.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <input
                        className="form-input"
                        type="text"
                        placeholder={overflow.destination_type === 'endpoint' ? 'Ex: 2000' : 'Destino'}
                        value={overflow.destination_overflow || ''}
                        onChange={(e) => handleUpdateWaitextenOverflow(index, 'destination_overflow', e.target.value)}
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>
      
      <hr className="section-divider" />
      
      <TimeConditionManager
        rules={config.menu_gotoiftime_rules || []}
        onChange={(rules) => handleChange('menu_gotoiftime_rules', rules)}
        availableContexts={availableContexts}
        disabled={false}
        title="Desvios por Data/Hora do Menu"
      />
      
      <hr className="section-divider" />
      
      <h4 className="section-title" style={{fontSize: '1.1rem'}}>
        <FontAwesomeIcon icon={faExchangeAlt} className="section-icon" />
        Ciclo / Repetição
      </h4>
      
      <div className="form-checkbox-wrapper">
        <input
          type="checkbox"
          id="cycle-enabled"
          checked={config.cycle_enabled || false}
          onChange={(e) => handleChange('cycle_enabled', e.target.checked)}
        />
        <label htmlFor="cycle-enabled">Habilitar Ciclo (Repetir Menu)</label>
      </div>
      <p className="form-help">Se habilitado, o menu será repetido até atingir o máximo de tentativas</p>
      
      {config.cycle_enabled && (
        <div className="form-field">
          <label className="form-label">Máximo de Tentativas</label>
          <input
            className="form-input"
            type="number"
            min="0"
            max="10"
            value={config.max_retries || 3}
            onChange={(e) => handleChange('max_retries', parseInt(e.target.value))}
          />
          <p className="form-help">Número máximo de tentativas antes de seguir para a ação de timeout (0-10)</p>
        </div>
      )}
    </div>
  );
}

export default MenuConfiguration;

